import { createContext, useCallback, useContext, useEffect, useRef, useState, type DependencyList } from 'react';

export type TokenGetter = () => Promise<string | null>;

export interface AuthState {
  mode: 'clerk' | 'admin-token';
  getToken: TokenGetter;
  /** Changes whenever the active organization changes, so data can be refetched. */
  scope: string;
  signOut: () => void;
}

export const AuthContext = createContext<AuthState | null>(null);

export function useAuthState(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('AuthContext missing');
  return ctx;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

export async function apiFetch<T>(getToken: TokenGetter, path: string, init: RequestInit = {}): Promise<T> {
  const token = await getToken();
  const headers = new Headers(init.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  const res = await fetch(`/api${path}`, { ...init, headers });
  if (res.status === 204) return undefined as T;
  const body = (await res.json().catch(() => null)) as { error?: string; message?: string } | null;
  if (!res.ok) {
    throw new ApiError(res.status, body?.error ?? 'error', body?.message ?? `Request failed with status ${res.status}.`);
  }
  return body as T;
}

export function useApiClient() {
  const { getToken } = useAuthState();
  return useCallback(<T,>(path: string, init?: RequestInit) => apiFetch<T>(getToken, path, init), [getToken]);
}

export interface Query<T> {
  data?: T;
  error?: Error;
  loading: boolean;
  reload: () => void;
}

/** Fetches `path` whenever it (or `deps`) change; `null` skips the request. */
export function useApi<T>(path: string | null, deps: DependencyList = []): Query<T> {
  const api = useApiClient();
  const { scope } = useAuthState();
  const [state, setState] = useState<{ data?: T; error?: Error; loading: boolean }>({ loading: path !== null });
  const [nonce, setNonce] = useState(0);
  const requestId = useRef(0);

  useEffect(() => {
    if (path === null) {
      setState({ loading: false });
      return;
    }
    const id = ++requestId.current;
    setState((s) => ({ data: s.data, loading: true }));
    api<T>(path)
      .then((data) => {
        if (id === requestId.current) setState({ data, loading: false });
      })
      .catch((error: Error) => {
        if (id === requestId.current) setState({ error, loading: false });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, nonce, scope, api, ...deps]);

  return { ...state, reload: () => setNonce((n) => n + 1) };
}

/** Downloads a file from the API (signed in) and saves it under the name the server gives, or `fallbackName`. */
export async function downloadFile(getToken: TokenGetter, path: string, fallbackName: string): Promise<void> {
  const token = await getToken();
  const res = await fetch(`/api${path}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string; message?: string } | null;
    throw new ApiError(res.status, body?.error ?? 'error', body?.message ?? `Download failed with status ${res.status}.`);
  }
  const name = /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') ?? '')?.[1] ?? fallbackName;
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
